import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  Label,
  Dropdown,
  IDropdownOption,
  Text,
  IconButton,
  ComboBox,
  IComboBoxOption,
} from '@fluentui/react';
import { IBestellung, IBestellungMitPositionen, GenehmigungsStatus, BestellStatus } from '../../shared/models';
import { bestellungenApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { centeredModalStyles, modalContentStyles } from '../styles/dialogs';
import axios from 'axios';

interface IBestellungFormularProps {
  isOpen: boolean;
  onDismiss: () => void;
  onSave: (newBestellungId?: number) => void;
  bestellung?: IBestellung;
}

interface IUserProfileSettings {
  personalnummer?: string;
  anforderndeKostenstelle?: string;
  durchwahl?: string;
}

export const BestellungFormular: React.FC<IBestellungFormularProps> = ({
  isOpen,
  onDismiss,
  onSave,
  bestellung,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<IBestellung>>({
    Title: '',
    AnforderndePersonen: '',
    Personalnummer: '',
    AnforderndeKostenstelle: '',
    Durchwahl: '',
    HinweistextZurBestellung: '',
  });

  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [profileDefaults, setProfileDefaults] = useState<IUserProfileSettings>({});

  // Vorlagen-Auswahl
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateFilterText, setTemplateFilterText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<IBestellungMitPositionen | null>(null);
  const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (bestellung) {
        setFormData(bestellung);
      } else {
        loadProfileDefaults();
        loadTemplates();
      }
    }
  }, [isOpen, bestellung, user]);

  const loadProfileDefaults = async () => {
    try {
      const response = await axios.get('/api/users/profile-settings/me', {
        params: { email: user?.email }
      });
      const defaults = response.data || {};
      setProfileDefaults(defaults);
      // Wenn keine Vorlage ausgewählt ist, verwende die Defaults sofort
      if (!selectedTemplate) {
        resetFormWithDefaults(defaults);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Profil-Defaults:', err);
      setProfileDefaults({});
      resetFormWithDefaults({});
    }
  };

  const resetFormWithDefaults = (defaults: IUserProfileSettings) => {
    setFormData({
      Title: '',
      AnforderndePersonen: user?.displayName || '',
      Personalnummer: defaults.personalnummer || '',
      AnforderndeKostenstelle: defaults.anforderndeKostenstelle || '',
      Durchwahl: defaults.durchwahl || '',
      HinweistextZurBestellung: '',
    });
    setSelectedTemplate(null);
    setTemplateFilterText('');
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const templates = await bestellungenApi.getAllForTemplates();
      setAvailableTemplates(templates);
    } catch (err) {
      console.error('Fehler beim Laden der Vorlagen:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadTemplateDetails = async (templateId: number) => {
    try {
      setLoadingTemplateDetails(true);
      // Lade vollständige Bestellung mit Positionen
      let templateDetails: IBestellungMitPositionen;

      // Prüfe ob archiviert oder aktiv
      const template = availableTemplates.find(t => t.Id === templateId);
      if (template?.IsArchiviert) {
        const response = await fetch(`/api/bestellungen/archived/${templateId}`, {
          credentials: 'include'
        });
        templateDetails = await response.json();
      } else {
        templateDetails = await bestellungenApi.getById(templateId);
      }

      setSelectedTemplate(templateDetails);

      // Übernehme Bestellungsdaten (ohne ID und Metadaten)
      setFormData({
        Title: templateDetails.Title || '',
        AnforderndePersonen: user?.displayName || templateDetails.AnforderndePersonen || '',
        Personalnummer: templateDetails.Personalnummer || '',
        AnforderndeKostenstelle: templateDetails.AnforderndeKostenstelle || '',
        Durchwahl: templateDetails.Durchwahl || '',
        HinweistextZurBestellung: templateDetails.HinweistextZurBestellung || '',
      });
    } catch (err: any) {
      setError('Fehler beim Laden der Vorlagen-Details: ' + (err.message || err));
    } finally {
      setLoadingTemplateDetails(false);
    }
  };

  const handleSave = async () => {
    // Validierung
    if (!formData.Title) {
      setError('Titel ist erforderlich');
      return;
    }
    if (!formData.AnforderndePersonen) {
      setError('Anfordernde Person ist erforderlich');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (bestellung?.Id) {
        // Update existing
        await bestellungenApi.update(bestellung.Id, formData);
        onSave();
        onDismiss();
      } else {
        // Create new
        const newBestellung = await bestellungenApi.create(formData);
        const newBestellungId = newBestellung.Id;

        // Wenn eine Vorlage ausgewählt wurde, kopiere die Positionen
        if (selectedTemplate && selectedTemplate.Positionen && newBestellungId) {
          for (const position of selectedTemplate.Positionen) {
            // Erstelle Position ohne ID und Metadaten
            const newPosition = {
              BestellungId: newBestellungId,
              PosID: position.PosID,
              EmpfangendeKostenstelle: position.EmpfangendeKostenstelle,
              Artikelname: position.Artikelname,
              Artikelnummer: position.Artikelnummer,
              BCNummer: position.BCNummer,
              Artikelbeschreibung: position.Artikelbeschreibung,
              Artikelhyperlink: position.Artikelhyperlink,
              Menge: position.Menge,
              Einzelpreis: position.Einzelpreis,
              PreisGesamt: position.PreisGesamt,
              Mengeneinheit: position.Mengeneinheit,
              KreditorString: position.KreditorString,
              Sachkonto: position.Sachkonto,
              InventarAnlagennummer: position.InventarAnlagennummer,
              LieferterminKW: position.LieferterminKW,
              Genehmigungsstatus: GenehmigungsStatus.Offen, // Reset Status
              Bestellstatus: BestellStatus.Eingereicht, // Reset Status
            };

            await bestellungenApi.createPosition(newBestellungId, newPosition);
          }
        }

        // Callback mit neuer Bestellungs-ID aufrufen
        onSave(newBestellungId);
        onDismiss();
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern der Bestellung');
    } finally {
      setSaving(false);
    }
  };

  // Gefilterte Vorlagen-Optionen
  const filteredTemplateOptions = useMemo(() => {
    const baseOptions = availableTemplates.map((t) => ({
      key: t.Id?.toString() || '',
      text: `${t.Title || 'Ohne Titel'} (${t.InterneBestellnummer || t.Id}) - ${t.AnforderndePersonen || ''}${t.IsArchiviert ? ' [Archiv]' : ''}`,
      data: t,
    }));

    if (!templateFilterText) {
      return baseOptions;
    }

    const searchLower = templateFilterText.toLowerCase();
    return baseOptions.filter((option) => {
      const template = option.data;
      return (
        template.Title?.toLowerCase().includes(searchLower) ||
        template.InterneBestellnummer?.toLowerCase().includes(searchLower) ||
        template.AnforderndePersonen?.toLowerCase().includes(searchLower) ||
        template.AnforderndeKostenstelle?.toLowerCase().includes(searchLower)
      );
    });
  }, [availableTemplates, templateFilterText]);

  const headerText = bestellung ? 'Bestellung bearbeiten' : 'Neue Bestellung';

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      isBlocking={true}
      styles={centeredModalStyles}
    >
      {/* Header */}
      <div style={modalContentStyles.header}>
        <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
          {headerText}
        </Text>
        <IconButton
          iconProps={{ iconName: 'Cancel' }}
          ariaLabel="Schließen"
          onClick={onDismiss}
          disabled={saving}
        />
      </div>

      {/* Body */}
      <div style={modalContentStyles.body}>
      <Stack tokens={{ childrenGap: 20 }}>
        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(null)}>
            {error}
          </MessageBar>
        )}

        {/* Vorlagen-Auswahl (nur bei neuer Bestellung) */}
        {!bestellung && (
          <>
            <MessageBar messageBarType={MessageBarType.info}>
              Sie können eine vorhandene Bestellung als Vorlage verwenden. Alle Daten (Grunddaten + Positionen) werden kopiert und können dann bearbeitet werden.
            </MessageBar>

            <ComboBox
              label="Daten aus alter Bestellung übernehmen (optional)"
              placeholder="Bestellung suchen..."
              options={filteredTemplateOptions}
              allowFreeform={true}
              autoComplete="on"
              onChange={(_, option) => {
                if (option) {
                  const templateId = parseInt(option.key as string, 10);
                  if (templateId) {
                    loadTemplateDetails(templateId);
                  }
                }
              }}
              onInputValueChange={(value) => {
                setTemplateFilterText(value || '');
              }}
              disabled={loadingTemplates || loadingTemplateDetails}
            />

            {loadingTemplateDetails && (
              <Spinner label="Lade Vorlagen-Details..." />
            )}

            {selectedTemplate && (
              <MessageBar messageBarType={MessageBarType.success}>
                Vorlage geladen: {selectedTemplate.AnzahlPositionen || 0} Position(en) werden kopiert
              </MessageBar>
            )}
          </>
        )}

        {/* Titel */}
        <TextField
          label="Titel der Bestellung"
          placeholder="z.B. Büromaterial Q1 2025"
          value={formData.Title || ''}
          onChange={(_, value) => setFormData((prev) => ({ ...prev, Title: value }))}
          required
          errorMessage={!formData.Title ? 'Titel ist erforderlich' : ''}
          styles={{
            field: { fontSize: '16px', fontWeight: 600 },
          }}
        />

        {/* Zeile: Anfordernde Person, Personalnummer, Durchwahl, Kostenstelle */}
        <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
          <TextField
            label="Anfordernde Person"
            placeholder="Nachname, Vorname"
            value={formData.AnforderndePersonen || ''}
            onChange={(_, value) => setFormData((prev) => ({ ...prev, AnforderndePersonen: value }))}
            required
            errorMessage={!formData.AnforderndePersonen ? 'Anfordernde Person ist erforderlich' : ''}
            styles={{ root: { width: '40%', minWidth: 200 } }}
          />
          <TextField
            label="Personalnummer"
            placeholder="z.B. 12345"
            value={formData.Personalnummer || ''}
            onChange={(_, value) => setFormData((prev) => ({ ...prev, Personalnummer: value }))}
            styles={{ root: { width: '20%', minWidth: 100 } }}
          />
          <TextField
            label="Durchwahl"
            placeholder="z.B. 123"
            value={formData.Durchwahl || ''}
            onChange={(_, value) => setFormData((prev) => ({ ...prev, Durchwahl: value }))}
            styles={{ root: { width: '20%', minWidth: 100 } }}
          />
          <TextField
            label="Kostenstelle"
            placeholder="z.B. 9430"
            value={formData.AnforderndeKostenstelle || ''}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, AnforderndeKostenstelle: value }))
            }
            styles={{ root: { width: '20%', minWidth: 100 } }}
          />
        </Stack>

        {/* Hinweistext */}
        <Stack tokens={{ childrenGap: 8 }}>
          <Label>Hinweis zur Bestellung</Label>
          <TextField
            placeholder="Optional: Zusätzliche Informationen zur Bestellung"
            value={formData.HinweistextZurBestellung || ''}
            onChange={(_, value) =>
              setFormData((prev) => ({ ...prev, HinweistextZurBestellung: value }))
            }
            multiline
            rows={3}
          />
        </Stack>

        {/* Info-Hinweis */}
        <MessageBar messageBarType={MessageBarType.info}>
          Nach dem Erstellen der Bestellung können Sie Positionen hinzufügen und weitere Details
          bearbeiten.
        </MessageBar>

        {saving && (
          <Stack horizontalAlign="center" tokens={{ padding: 20 }}>
            <Spinner label="Speichere Bestellung..." />
          </Stack>
        )}
      </Stack>
      </div>

      {/* Footer */}
      <div style={modalContentStyles.footer}>
        <DefaultButton onClick={onDismiss} disabled={saving} text="Abbrechen" />
        <PrimaryButton onClick={handleSave} disabled={saving} text="Speichern" />
      </div>
    </Modal>
  );
};
